import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/stores/auth-store'
import { useConfigurationStore } from '@/stores/configuration-store'
import { useCustomerStore } from '@/stores/customer-store'
import { useDevicesStore } from '@/stores/devices-store'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

interface FormValues {
  assistentName: string
  smsSender: string
  persona: string
}

const schema = yup.object({
  assistentName: yup
    .string()
    .trim()
    .required('Dieses Feld ist erforderlich')
    .max(30, 'Maximal 30 Zeichen erlaubt'),
  smsSender: yup
    .string()
    .trim()
    .required('Dieses Feld ist erforderlich')
    .matches(
      /^[a-zA-Z0-9-.,!]+$/,
      'Nur Buchstaben, Zahlen und folgende Zeichen: - . , !'
    )
    .max(12, 'Maximal 12 Zeichen erlaubt'),
  persona: yup.string().required('Bitte wählen Sie eine Persona'),
})

export function DeviceBasicSettings() {
  const { t } = useTranslation()
  const { currentDevice, updateConfigData, getData } = useDevicesStore()
  const { getPersonas, retrievePersona } = useConfigurationStore()
  const { canEdit } = useAuthStore()
  const { reset: resetCustomer } = useCustomerStore()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [availablePersonas, setAvailablePersonas] = useState<any[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
    watch,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      assistentName: '',
      smsSender: '',
      persona: '',
    },
  })

  const personaValue = watch('persona')

  // Load data on mount and when device changes
  useEffect(() => {
    if (currentDevice?.deviceId) {
      loadData()
    }
  }, [currentDevice?.deviceId])

  const loadData = async () => {
    if (!currentDevice || !currentDevice.deviceConfiguration) {
      console.log('❌ DeviceBasicSettings: No device or config')
      return
    }

    console.log('🔄 DeviceBasicSettings: Loading data for device:', currentDevice.deviceId)

    // Load field values from device configuration
    const assistentName = String(getData('LOCATION_NAME') || '')
    const smsSender = String(getData('SMS_SENDER_NAME') || '')
    const personaId = String(getData('PERSONA_ID') || '')
    const language = String(getData('LANGUAGE') || 'de')

    console.log('✅ DeviceBasicSettings: Loaded data:', { assistentName, smsSender, personaId, language })

    reset({
      assistentName,
      smsSender,
      persona: personaId,
    })

    // Load personas for current language
    try {
      const { useAuthStore } = await import('@/stores/auth-store')
      const headers = useAuthStore.getState().getApiHeaders()

      if (!getPersonas(language)) {
        await retrievePersona(language, headers)
      }

      const personas = getPersonas(language)
      const personasArray = Array.isArray(personas) ? personas : []
      setAvailablePersonas(personasArray)
      console.log('✅ DeviceBasicSettings: Loaded personas:', personasArray)
    } catch (error) {
      console.error('Error loading personas:', error)
      toast({
        variant: 'destructive',
        title: t('error.title'),
        description: t('deviceBasicSettings.errorLoadingPersonas'),
      })
    }
  }

  const onSubmit = async (data: FormValues) => {
    if (!isDirty) return

    setIsLoading(true)

    try {
      // Save all three fields in parallel
      await Promise.all([
        updateConfigData('LOCATION_NAME', data.assistentName),
        updateConfigData('SMS_SENDER_NAME', data.smsSender),
        updateConfigData('PERSONA_ID', data.persona),
      ])

      // Reset customer store to reload with new config
      await resetCustomer()

      toast({
        title: t('success.title'),
        description: t('deviceBasicSettings.saveSuccess'),
      })

      // Reset form dirty state
      reset(data)
    } catch (error) {
      console.error('Error saving settings:', error)
      toast({
        variant: 'destructive',
        title: t('error.title'),
        description: t('deviceBasicSettings.saveError'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isDisabled = !canEdit() || isLoading

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Assistant Name Field */}
        <div className="space-y-2">
          <Label htmlFor="assistentName" className="text-sm font-semibold text-gray-700">
            {t('configuration.device.base.labelTitle')}
          </Label>
          <Input
            id="assistentName"
            {...register('assistentName')}
            placeholder={t('configuration.device.base.placeholderTitle')}
            disabled={isDisabled}
            className={errors.assistentName ? 'border-red-300' : ''}
          />
          {errors.assistentName && (
            <p className="text-sm text-red-600 font-medium">
              {errors.assistentName.message}
            </p>
          )}
          <p className="text-xs text-gray-500">
            {t('deviceBasicSettings.nameHint')}
          </p>
        </div>

        {/* SMS Sender Field */}
        <div className="space-y-2">
          <Label htmlFor="smsSender" className="text-sm font-semibold text-gray-700">
            {t('configuration.device.base.labelSmsSender')}
          </Label>
          <Input
            id="smsSender"
            {...register('smsSender')}
            placeholder={t('configuration.device.base.placeholderSms')}
            disabled={isDisabled}
            maxLength={12}
            className={errors.smsSender ? 'border-red-300' : ''}
          />
          {errors.smsSender && (
            <p className="text-sm text-red-600 font-medium">
              {errors.smsSender.message}
            </p>
          )}
          <p className="text-xs text-gray-500">
            {t('deviceBasicSettings.smsHint')}
          </p>
        </div>

        {/* Persona Field */}
        <div className="space-y-2">
          <Label htmlFor="persona" className="text-sm font-semibold text-gray-700">
            {t('configuration.device.base.labelPersona')}
          </Label>
          <Select
            value={personaValue}
            onValueChange={(value) => setValue('persona', value, { shouldDirty: true })}
            disabled={isDisabled}
          >
            <SelectTrigger className={errors.persona ? 'border-red-300' : ''}>
              <SelectValue placeholder={t('deviceBasicSettings.selectPersona')} />
            </SelectTrigger>
            <SelectContent>
              {(Array.isArray(availablePersonas) ? availablePersonas : []).map((persona) => (
                <SelectItem key={persona.id} value={persona.id}>
                  {persona.name || persona.displayTitle || persona.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.persona && (
            <p className="text-sm text-red-600 font-medium">
              {errors.persona.message}
            </p>
          )}
          <p className="text-xs text-gray-500">
            {t('deviceBasicSettings.personaHint')}
          </p>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            disabled={!isDirty || isDisabled}
            className="inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            {isLoading ? t('global.button.loading') : t('global.button.saveChanges')}
          </Button>
        </div>
      </form>
    </div>
  )
}
